import path from 'node:path';
import { getFramework } from '@bolt/framework-detection';
import { addWorkDir, removeWorkDir } from './workDir';
import { BOLT_BOOTSTRAP_ID } from '~/lib/constants';
import type { FileSystemSnapshot, Snapshot } from '~/lib/snapshots/snapshot';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import type { FileMap } from '~/lib/stores/files';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { getBuildConfig } from '~/templates';
import type { AssistantMessage, Message } from '~/types/chat';

export async function createSnapshot(messages: Message[], restorePoint?: Message['id']) {
  const lastAssistantResponse = messages.findLast(({ id, role }) => {
    return role === 'assistant' && id !== BOLT_BOOTSTRAP_ID;
  }) as AssistantMessage | undefined;

  if (lastAssistantResponse?.error) {
    return false;
  }

  const messageId = restorePoint ?? lastAssistantResponse?.messageId ?? lastAssistantResponse?.id;
  const projectId = projectStore.getProjectId();
  const hasSnapshot = snapshotManager.hasSnapshot(messageId);

  if (!messageId || !projectId || hasSnapshot) {
    return false;
  }

  // lets find the previous snaphsot
  let previousSnapshot: Snapshot | undefined;

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];

    if (message.role === 'assistant' && message.id !== BOLT_BOOTSTRAP_ID) {
      if (message.id === messageId) {
        continue;
      }

      if (snapshotManager.hasSnapshot(message.id)) {
        previousSnapshot = snapshotManager.getSnapshot(message.id);

        break;
      }
    }
  }

  const newSnapshot = toFileSystemSnapshot(workbenchStore.files.get());

  if (previousSnapshot) {
    const snapshot = await previousSnapshot.fetch();

    if (areSnapshotsIdentical(snapshot, newSnapshot)) {
      // if the snapshot is identical, we don't need to create a new one
      return false;
    }
  }

  snapshotManager.create(projectId, messageId, newSnapshot);

  return true;
}

function toFileSystemSnapshot(files: FileMap) {
  // let's infer the output directory based on the files
  const outputDirectory = getOutputDirectoryFromConfig(files);

  const filesystem: FileSystemSnapshot = {};

  const directories = new Set<string>();

  for (const [filePath, dirent] of Object.entries(files)) {
    if (!dirent) {
      continue;
    }

    const filename = removeWorkDir(filePath);

    if (dirent.type === 'folder') {
      if (filename === outputDirectory || filename.startsWith(`${outputDirectory}/`)) {
        // we don't add the output directory or any folders from the output directory
        continue;
      }

      // we only add directories if they are empty
      if (!directories.has(filename)) {
        filesystem[filename] = {
          type: dirent.type,
        };
      }
    } else {
      if (filename.startsWith(`${outputDirectory}/`)) {
        // we don't add any files from the output directory
        continue;
      }

      const dirname = path.dirname(filename);

      directories.add(dirname);

      filesystem[filename] = {
        type: dirent.type,
        content: dirent.content,
        ...(dirent.isBinary && { isBinary: true }),
        ...(dirent.blobId && { blobId: dirent.blobId }),
      };

      if (filesystem[dirname] && filesystem[dirname].type === 'folder') {
        // we delete non-empty directories
        delete filesystem[dirname];
      }
    }
  }

  return filesystem;
}

function areSnapshotsIdentical(a: FileSystemSnapshot, b: FileSystemSnapshot) {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  for (let i = 0; i < aKeys.length; i++) {
    const key = aKeys[i];

    if (key !== bKeys[i]) {
      return false;
    }

    const fileA = a[key];
    const fileB = b[key];

    if (fileA?.type !== fileB?.type) {
      return false;
    }

    if (fileA?.type === 'file' && fileB?.type === 'file') {
      if (fileA?.content !== fileB?.content) {
        return false;
      }
    }
  }

  return true;
}

function getOutputDirectoryFromConfig(files: FileMap) {
  let outputDirectory = 'dist';

  try {
    // we first try to infer it based on the template
    const boltConfigJson = tryReadJson(files, '.bolt/config.json');

    if (boltConfigJson && 'template' in boltConfigJson) {
      outputDirectory = getBuildConfig(boltConfigJson.template)?.output ?? outputDirectory;
    }

    // if the template didn't help, we try to infer it based on the framework
    if (!outputDirectory) {
      const packageJson = tryReadJson(files, 'package.json');

      if (packageJson) {
        const framework = getFramework(packageJson);

        outputDirectory = framework?.build?.output ?? outputDirectory;
      }
    }
  } catch {
    // ignore invalid config
  }

  // make sure the directory does not end with a trailing slash
  if (outputDirectory.endsWith('/')) {
    return outputDirectory.slice(0, -1);
  }

  return outputDirectory;
}

function tryReadJson(files: FileMap, path: string) {
  const file = files[addWorkDir(path)];

  if (file?.type === 'file') {
    try {
      return JSON.parse(file.content);
    } catch {
      return undefined;
    }
  }

  return undefined;
}
